if (!customElements.get('klarna-template-persistence')) {
  customElements.define('klarna-template-persistence', class KlarnaTemplatePersistence extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'klarnaTemplateData';
      this.init();
    }

    init() {
      if (!window.location.pathname.includes('/collections/')) return;

      const pathParts = window.location.pathname.split('/');
      const collectionIndex = pathParts.indexOf('collections');
      if (collectionIndex === -1 || collectionIndex + 1 >= pathParts.length) return;
      
      const currentCollectionHandle = pathParts[collectionIndex + 1];

      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');
      
      if (viewParam === 'klarna') {
        this.saveKlarnaTemplateData(currentCollectionHandle, 'klarna');
      } else {
        this.applyKlarnaTemplateIfNeeded(currentCollectionHandle);
      }

      this.updatePageLinks();
      this.observePageChanges();
      this.handleFacetForms();
    }
    
    saveKlarnaTemplateData(collectionHandle, templateSuffix) {
      const data = {
        collectionHandle: collectionHandle,
        templateSuffix: templateSuffix,
        timestamp: Date.now()
      };
      
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    }
    
    getKlarnaTemplateData() {
      const storedData = localStorage.getItem(this.storageKey);
      if (!storedData) return null;
      
      try {
        const parsedData = JSON.parse(storedData);

        const oneDayInMs = 24 * 60 * 60 * 1000;
        const currentTime = Date.now();
        
        if (parsedData.timestamp && (currentTime - parsedData.timestamp > oneDayInMs)) {
          localStorage.removeItem(this.storageKey);
          return null;
        }
        
        return parsedData;
      } catch (error) {
        console.error('Error parsing Klarna template data:', error);
        localStorage.removeItem(this.storageKey);
        return null;
      }
    }
    
    applyKlarnaTemplateIfNeeded(currentCollectionHandle) {
      const storedData = this.getKlarnaTemplateData();
      
      if (storedData && 
          storedData.collectionHandle === currentCollectionHandle && 
          storedData.templateSuffix === 'klarna') {

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('view') !== 'klarna') {
          urlParams.set('view', 'klarna');

          const newUrl = `${window.location.pathname}?${urlParams.toString()}${window.location.hash}`;
          window.location.href = newUrl;
        }
      }
    }
    
    updatePageLinks() {
      const storedData = this.getKlarnaTemplateData();
      if (!storedData || storedData.templateSuffix !== 'klarna') return;

      const collectionLinks = document.querySelectorAll(`a[href*="/collections/${storedData.collectionHandle}"]`);
      
      collectionLinks.forEach(link => {
        const url = new URL(link.href, window.location.origin);
        const urlParams = new URLSearchParams(url.search);

        if (urlParams.get('view') === 'klarna') return;

        if (url.pathname.includes(`/collections/${storedData.collectionHandle}`)) {
          urlParams.set('view', 'klarna');
          url.search = urlParams.toString();
          link.href = url.toString();
        }
      });
    }
    
    handleFacetForms() {
      const storedData = this.getKlarnaTemplateData();
      if (!storedData || storedData.templateSuffix !== 'klarna') return;

      const facetForms = document.querySelectorAll('form[is="facets-form"]');
      
      facetForms.forEach(form => {
        let viewInput = form.querySelector('input[name="view"]');
        
        if (!viewInput) {
          viewInput = document.createElement('input');
          viewInput.type = 'hidden';
          viewInput.name = 'view';
          viewInput.value = 'klarna';
          form.appendChild(viewInput);
        } else {
          viewInput.value = 'klarna';
        }

        form.addEventListener('submit', (event) => {
          const formData = new FormData(form);
          if (!formData.has('view') || formData.get('view') !== 'klarna') {
            formData.set('view', 'klarna');
          }
        });
      });

      this.observeForFacetControls();
    }
    
    observeForFacetControls() {
      const facetObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            const addedForms = Array.from(mutation.addedNodes)
              .filter(node => node.nodeType === Node.ELEMENT_NODE)
              .reduce((forms, node) => {
                if (node.matches && node.matches('form[is="facets-form"]')) {
                  forms.push(node);
                }
                
                const childForms = node.querySelectorAll ? 
                  node.querySelectorAll('form[is="facets-form"]') : [];
                  
                return forms.concat(Array.from(childForms));
              }, []);
              
            if (addedForms.length > 0) {
              this.handleFacetForms();
            }
          }
        });
      });
      
      facetObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      this.facetObserver = facetObserver;
    }
    
    observePageChanges() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            this.updatePageLinks();
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.observer = observer;
    }
    
    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      
      if (this.facetObserver) {
        this.facetObserver.disconnect();
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const klarnaTemplatePersistence = document.createElement('klarna-template-persistence');
  document.body.appendChild(klarnaTemplatePersistence);
});